/**
 * PropertyDisclaimer
 *
 * The close of every property detail page, above the footer.
 *
 * Owner, 2026-10-07: the end of the page was cramped (a boxed safety strip with
 * an icon tile, then two paragraphs of small grey legal text and two footnotes,
 * straight into the footer). It is now one short safety note with the report
 * link, and the legal notice folded under «إخلاء المسؤولية ومعلومات الإعلان»:
 * the reference, the advertiser-is-responsible notice (the Rightmove-style
 * neutral-host shield, retargeted to REGA/FAL), the REGA line, the links to the
 * full terms and the data footnotes. It is still on every page and in the HTML,
 * one click away. Fully bilingual + RTL via the `property` namespace.
 */

import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { Property } from '../../mocks/properties';

interface PropertyDisclaimerProps {
  property: Property;
}

const LINK =
  'font-medium text-[var(--pv-color-primary)] underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--pv-color-primary)]/30';

export default function PropertyDisclaimer({ property }: PropertyDisclaimerProps) {
  const { t, i18n } = useTranslation(['property']);
  const isArabicLang = i18n.language === 'ar';

  const reference = property.adLicenseNumber || `SQ-${property.id.slice(0, 8).toUpperCase()}`;
  const advertiser =
    (isArabicLang
      ? property.agentNameAr || property.agentName
      : property.agentName || property.agentNameAr) ||
    t('property.disclaimer.advertiserFallback');

  return (
    <section
      className="border-t border-[var(--pv-border-subtle)]"
      dir={i18n.dir()}
      aria-label={t('property.disclaimer.heading')}
    >
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
        <div className="max-w-3xl text-start">
          <h2 className="m-0 text-[1rem] font-semibold text-[var(--pv-color-text)]">
            {t('property.disclaimer.safety.title')}
          </h2>
          <p className="m-0 mt-1 text-[0.9375rem] leading-relaxed text-[var(--pv-color-text-muted)]">
            {t('property.disclaimer.safety.body')}
          </p>
          <Link
            to={`/complaints?propertyId=${property.id}`}
            className={`mt-2 inline-flex items-center gap-1.5 text-[0.9375rem] font-semibold ${LINK}`}
          >
            <i className="ri-flag-line text-[1rem] leading-none" aria-hidden="true"></i>
            {t('property.disclaimer.safety.report')}
          </Link>

          <details className="group mt-6 border-t border-[var(--pv-border-subtle)] pt-4">
            <summary className="inline-flex cursor-pointer list-none items-center gap-1 rounded text-[0.875rem] font-medium text-[var(--pv-color-text-muted)] transition-colors hover:text-[var(--pv-color-text)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--pv-color-primary)]/30 [&::-webkit-details-marker]:hidden">
              {t('property.disclaimer.heading')}
              <i
                className="ri-arrow-down-s-line text-[1.125rem] leading-none transition-transform group-open:rotate-180 motion-reduce:transition-none"
                aria-hidden="true"
              ></i>
            </summary>
            <div className="mt-3 space-y-3 text-[0.8125rem] leading-relaxed text-[var(--pv-color-text-muted)]">
              <p className="m-0">
                <span className="font-semibold text-[var(--pv-color-text)]">
                  {t('property.disclaimer.reference', { ref: reference })}.
                </span>{' '}
                {t('property.disclaimer.body', { advertiser })}
              </p>
              <p className="m-0">
                {t('property.disclaimer.rega')}
                {property.adLicenseNumber ? (
                  <>
                    {' '}
                    <span className="font-semibold text-[var(--pv-color-text)]" dir="ltr">
                      {t('property.disclaimer.adLicense', { number: property.adLicenseNumber })}
                    </span>
                  </>
                ) : null}
              </p>
              <p className="m-0">
                {t('property.disclaimer.linksLabel')}{' '}
                <Link to="/terms" className={LINK}>
                  {t('property.disclaimer.termsLink')}
                </Link>
                <span className="mx-1.5" aria-hidden="true">·</span>
                <Link to="/fal-disclaimer" className={LINK}>
                  {t('property.disclaimer.falLink')}
                </Link>
              </p>
              <p className="m-0">{t('property.disclaimer.footnoteData')}</p>
              <p className="m-0">{t('property.disclaimer.footnoteMap')}</p>
            </div>
          </details>
        </div>
      </div>
    </section>
  );
}
